/**
 * Price calculation utilities for the Star Deli app
 * Provides fallback JavaScript implementations for WebAssembly functions
 */

// Constants for JavaScript fallback pricing
const BASE_PRICE = 8.99;

const CHEESE_PRICE = 1.50;

const PREMIUM_BREAD_PRICE = 1.0;

// Style constants

const STYLE_SPECIAL = 2;
const STYLE_SUPER = 3;
const STYLE_NEW_YORKER = 4;

// Style price adjustments
const STYLE_SPECIAL_PRICE = 3.00;
const STYLE_SUPER_PRICE = 3.00;
const STYLE_NEW_YORKER_PRICE = 5.00;

// JavaScript fallback for sandwich price calculation
export const calculateSandwichPriceJS = (config, menuData) => {
  // Extract menu items
  const { meats, cheeses, toppings, breads } = menuData;

  // Find selected meat and variant
  const selectedMeat = config.meat_id 
    ? meats.find(meat => meat.id === config.meat_id) 
    : null;
    
  // Calculate base price from meat selection including variant adjustment
  let totalPrice = BASE_PRICE;
  
  if (selectedMeat) {
    totalPrice = selectedMeat.base_price;
    
    // Apply meat variant price adjustment if specified
    if (config.meat_variant_id && selectedMeat.variants) {
      const variant = selectedMeat.variants.find(v => v.id === config.meat_variant_id);
      if (variant) {
        totalPrice += variant.price_adjustment;
      }
    }
    
    // Check for combo meats - special combo pricing
    if (config.double_meat) {
      const primaryMeatId = config.meat_id;
      
      // Check for special Corned Beef & Chop Liver combo - special fixed price of $17.99
      // Corned Beef ID: 1, Chop Liver ID: 11
      if ((primaryMeatId === 1 && config.secondary_meat_id === 11) || 
          (primaryMeatId === 11 && config.secondary_meat_id === 1)) {
        totalPrice = 17.99; // Special combo price override
      } else {
        // For regular combos, find the secondary meat
        const secondaryMeat = meats.find(meat => meat.id === config.secondary_meat_id);
        
        if (secondaryMeat) {
          // Calculate the secondary meat price with its variant
          let secondaryMeatPrice = secondaryMeat.base_price;
          
          // Apply secondary meat variant adjustment if applicable
          if (config.secondary_meat_variant_id && secondaryMeat.variants) {
            const secondaryVariant = secondaryMeat.variants.find(
              v => v.id === config.secondary_meat_variant_id
            );
            if (secondaryVariant) {
              secondaryMeatPrice += secondaryVariant.price_adjustment;
            }
          }
          
          // For regular combos, use ONLY the higher priced meat
          if (secondaryMeatPrice > totalPrice) {
            totalPrice = secondaryMeatPrice;
          }
          // DO NOT add the prices together for combos
        }
      }
    }
  }

  // Handle style pricing
  if (config.style_id) {
    switch (config.style_id) {
      case STYLE_SPECIAL:
        // Special style includes Swiss cheese and coleslaw for a fixed price
        totalPrice += STYLE_SPECIAL_PRICE;
        
        // If user selected a different cheese than Swiss (id 1), add it
        if (config.cheese_id && config.cheese_id !== 1) { // Not Swiss
          const selectedCheese = cheeses.find(cheese => cheese.id === config.cheese_id);
          if (selectedCheese) {
            totalPrice += selectedCheese.price || CHEESE_PRICE;
          }
        }
        
        // Check if coleslaw (id 3) is missing and apply discount if it is
        const hasColeslaw = config.topping_ids?.includes(3) || false;
        if (!hasColeslaw) {
          // Find coleslaw price and discount accordingly
          const coleslaw = toppings.find(t => t.id === 3);
          if (coleslaw && coleslaw.price > 0) {
            totalPrice -= coleslaw.price; // Apply discount for removing included coleslaw
          }
        }
        
        // Add non-included toppings
        if (config.topping_ids && config.topping_ids.length > 0) {
          config.topping_ids.forEach(toppingId => {
            // Skip coleslaw (id 3) as it's included
            if (toppingId !== 3) {
              const topping = toppings.find(t => t.id === toppingId);
              if (topping && topping.price > 0) {
                // Special handling for Extra Meat (ID 14)
                if (topping.id === 14 && config.extra_meat_quantity) {
                  // Apply the quantity multiplier to the extra meat price
                  totalPrice += topping.price * config.extra_meat_quantity;
                } else {
                  // Regular topping
                  totalPrice += topping.price;
                }
              }
            }
          });
        }
        break;
        
      case STYLE_SUPER:
        // Super style includes Swiss cheese, lettuce, and tomato
        totalPrice += STYLE_SUPER_PRICE;
        
        // If user selected a different cheese than Swiss (id 1), add it
        if (config.cheese_id && config.cheese_id !== 1) { // Not Swiss
          const selectedCheese = cheeses.find(cheese => cheese.id === config.cheese_id);
          if (selectedCheese) {
            totalPrice += selectedCheese.price || CHEESE_PRICE;
          }
        }
        
        // Check if lettuce (id 1) is missing and apply discount if it is
        const hasLettuce = config.topping_ids?.includes(1) || false;
        if (!hasLettuce) {
          // Lettuce is typically free, no discount needed
        }
        
        // Check if tomato (id 2) is missing and apply discount if it is
        const hasTomato = config.topping_ids?.includes(2) || false;
        if (!hasTomato) {
          // Find tomato price and discount accordingly
          const tomato = toppings.find(t => t.id === 2);
          if (tomato && tomato.price > 0) {
            totalPrice -= tomato.price; // Apply discount for removing included tomato
          }
        }
        
        // Add non-included toppings
        if (config.topping_ids && config.topping_ids.length > 0) {
          config.topping_ids.forEach(toppingId => {
            // Skip lettuce (1) and tomato (2) as they're included
            if (toppingId !== 1 && toppingId !== 2) {
              const topping = toppings.find(t => t.id === toppingId);
              if (topping && topping.price > 0) {
                // Special handling for Extra Meat (ID 14)
                if (topping.id === 14 && config.extra_meat_quantity) {
                  // Apply the quantity multiplier to the extra meat price
                  totalPrice += topping.price * config.extra_meat_quantity;
                } else {
                  // Regular topping
                  totalPrice += topping.price;
                }
              }
            }
          });
        }
        break;
        
      case STYLE_NEW_YORKER:
        // New Yorker style doubles the meat, but the price adjustment already includes this
        // Just add the fixed price adjustment
        totalPrice += STYLE_NEW_YORKER_PRICE;
        
        // New Yorker style ALWAYS charges for cheese, no included cheese
        if (config.cheese_id) {
          const selectedCheese = cheeses.find(cheese => cheese.id === config.cheese_id);
          if (selectedCheese) {
            totalPrice += selectedCheese.price || CHEESE_PRICE;
            
            // Apply extra cheese if selected
            if (config.extra_cheese) {
              totalPrice += (selectedCheese.price || CHEESE_PRICE) * 0.5;
            }
          }
        }
        
        // Add all toppings - New Yorker style always charges for toppings
        if (config.topping_ids && config.topping_ids.length > 0) {
          config.topping_ids.forEach(toppingId => {
            const topping = toppings.find(t => t.id === toppingId);
            if (topping && topping.price > 0) {
              // Special handling for Extra Meat (ID 14)
              if (topping.id === 14 && config.extra_meat_quantity) {
                // Apply the quantity multiplier to the extra meat price
                totalPrice += topping.price * config.extra_meat_quantity;
              } else {
                // Regular topping
                totalPrice += topping.price;
              }
            }
          });
        }
        
        // Add bread premium if applicable for New Yorker style
        if (config.bread_id) {
          const selectedBread = breads.find(bread => bread.id === config.bread_id);
          if (selectedBread && selectedBread.is_premium) {
            totalPrice += selectedBread.price_adjustment || PREMIUM_BREAD_PRICE;
          }
        }
        break;
        
      default:
        // Plain style or any other style
        // Note: We DON'T need to add anything here for double_meat (combo sandwiches)
        // as the combo pricing logic has already been handled above.
        // This code was incorrectly adding the meat price twice!
        
        // Add cheese
        if (config.cheese_id) {
          const selectedCheese = cheeses.find(cheese => cheese.id === config.cheese_id);
          if (selectedCheese) {
            totalPrice += selectedCheese.price || CHEESE_PRICE;
            
            // Apply extra cheese if selected
            if (config.extra_cheese) {
              totalPrice += (selectedCheese.price || CHEESE_PRICE) * 0.5;
            }
          }
        }
        
        // Add all toppings
        if (config.topping_ids && config.topping_ids.length > 0) {
          config.topping_ids.forEach(toppingId => {
            const topping = toppings.find(t => t.id === toppingId);
            if (topping && topping.price > 0) {
              // Special handling for Extra Meat (ID 14)
              if (topping.id === 14 && config.extra_meat_quantity) {
                // Apply the quantity multiplier to the extra meat price
                totalPrice += topping.price * config.extra_meat_quantity;
              } else {
                // Regular topping
                totalPrice += topping.price;
              }
            }
          });
        }
        
        // Add bread premium if applicable for Plain style
        if (config.bread_id) {
          const selectedBread = breads.find(bread => bread.id === config.bread_id);
          if (selectedBread && selectedBread.is_premium) {
            totalPrice += selectedBread.price_adjustment || PREMIUM_BREAD_PRICE;
          }
        }
    }
  } else {
    // No style selected, apply standard pricing
    // Note: We DON'T need to add anything here for double_meat (combo sandwiches)
    // as the combo pricing logic has already been handled above.
    // This code was incorrectly adding the meat price twice!
    
    // Add cheese
    if (config.cheese_id) {
      const selectedCheese = cheeses.find(cheese => cheese.id === config.cheese_id);
      if (selectedCheese) {
        totalPrice += selectedCheese.price || CHEESE_PRICE;
        
        // Apply extra cheese if selected
        if (config.extra_cheese) {
          totalPrice += (selectedCheese.price || CHEESE_PRICE) * 0.5;
        }
      }
    }
    
    // Add all toppings
    if (config.topping_ids && config.topping_ids.length > 0) {
      config.topping_ids.forEach(toppingId => {
        const topping = toppings.find(t => t.id === toppingId);
        if (topping && topping.price > 0) {
          // Special handling for Extra Meat (ID 14)
          if (topping.id === 14 && config.extra_meat_quantity) {
            // Apply the quantity multiplier to the extra meat price
            totalPrice += topping.price * config.extra_meat_quantity;
          } else {
            // Regular topping
            totalPrice += topping.price;
          }
        }
      });
    }
    
    // Add bread premium if applicable for no style case
    if (config.bread_id) {
      const selectedBread = breads.find(bread => bread.id === config.bread_id);
      if (selectedBread && selectedBread.is_premium) {
        totalPrice += selectedBread.price_adjustment || PREMIUM_BREAD_PRICE;
      }
    }
  }
  
  // Add pricing for dressings
  if (config.dressing_ids && config.dressing_ids.length > 0) {
    // First two dressings are free, additional ones are $1.25 each
    const extraDressingsCount = Math.max(0, config.dressing_ids.length - 2);
    if (extraDressingsCount > 0) {
      totalPrice += extraDressingsCount * 1.25;
    }
  }

  // Add bread premium if applicable
  if (config.bread_id) {
    const selectedBread = breads.find(bread => bread.id === config.bread_id);
    if (selectedBread && selectedBread.is_premium) {
      totalPrice += selectedBread.price_adjustment || PREMIUM_BREAD_PRICE;
    }
  }

  // Round to two decimal places
  return Math.round(totalPrice * 100) / 100;
};

// JavaScript fallback for combo price calculation
export const calculateComboPriceJS = (sandwichPrice, drinkId, sideId) => {
  // Define drink prices
  const drinkPrices = [
    { id: 1, price: 1.99 }, // Small
    { id: 2, price: 2.49 }, // Medium
    { id: 3, price: 2.99 }, // Large
  ];

  // Define side prices
  const sidePrices = [
    { id: 1, price: 1.99 }, // Chips
    { id: 2, price: 2.99 }, // Potato salad
    { id: 3, price: 3.49 }, // Coleslaw
  ];

  let total = sandwichPrice;

  // Add drink price
  const selectedDrink = drinkPrices.find(drink => drink.id === drinkId);
  if (selectedDrink) {
    total += selectedDrink.price;
  }

  // Add side price
  const selectedSide = sidePrices.find(side => side.id === sideId);
  if (selectedSide) {
    total += selectedSide.price;
  }

  // Apply combo discount (10%)
  total *= 0.9;

  // Round to two decimal places
  return Math.round(total * 100) / 100;
};

// JavaScript fallback for bulk price calculation
export const bulkPriceCalculationJS = (configs, menuData) => {
  return configs.map(config => calculateSandwichPriceJS(config, menuData));
};

// Helper function to format price as currency
export const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(price);
};